// oxlint-disable react/react-compiler -- TODO(react-compiler): Compilation Skipped (incompatible library API); the compiler deliberately declines to memoize this component. Informational; review usage of the flagged API.
// oxlint-disable react/rule-suppression -- Several effects intentionally suppress exhaustive-deps to gate on refs / initial mounts; each is reviewed with its own inline rationale.
import { toInspectRegions, useHelmInspect } from '@features/fields/api/useHelmInspect'
import { buildLinePreviewRequest, useHelmPath } from '@features/fields/api/useHelmPath'
import { type GuidanceDraft, useSaveGuidancePlan } from '@features/fields/api/useSaveGuidancePlan'
import { type SavedPrimitive, useSavePrimitive } from '@features/fields/api/useSavePrimitives'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import type { RegionCollection } from 'api-validations/geojson-schemas'
import {
  getBoundaryApi,
  getFieldPlansByIdApi,
  listGuidancePrimitiveApi
} from 'api-validations/locutus'
import {
  DEFAULT_GLOBAL_SETTINGS,
  DEFAULT_GUIDANCE_PROPERTIES,
  GuidancePrimitive,
  guidancePlanSchema,
  type PropagatableGuidancePrimitive
} from 'api-validations/locutus/guidance'
import type { FeatureCollection, LineString, Point } from 'geojson'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaQuery } from 'shared-ui/api-provider'
import shockedCorn from 'shared-ui/assets/shocked-corn.svg'
import { ButtonKind, CircleSpinner, IconRotation, PanoramaButton } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { MeasureSystem, useLocalization } from 'shared-ui/localization'
import {
  buildPlanPropagationRequest,
  createZone,
  DrawMode,
  DrawScene,
  DrawStyle,
  deleteZone,
  getZoneById,
  planZoneColor,
  resetZoneEditing,
  restoreZones,
  setDrawScene,
  useCurrentZones,
  useMapContext,
  useMapListener,
  useSelectedId,
  ZONE_PALETTE
} from 'shared-ui/map'
import { useDebouncedValue } from 'shared-ui/util-hooks'
import { convertFromImperial, convertToImperial, Geo, isNil } from 'utils'

import { PropagationLegend } from '../PropagationLegend'
import { PRESCRIPTION_BASE_ZONE_ID, withZone } from '../ReusedComponents'
import { Carve } from './Carve'
import { Details } from './Details'
import { buildDraftGuidancePlan, overrideSettingsFromPlan } from './draftGuidancePlan'
import type { FormValues, ZoneGuidance } from './formtypes'
import { LineList, type LineRow } from './LineList'
import { type OverridePopupState, OverrideMapPopup } from './OverrideMapPopup'
import { Overrides } from './Overrides'
import { savedPrimitiveToLine } from './shared'
import {
  type Draft,
  DraftMode,
  enterStep,
  isIdle,
  type LinesActivity,
  LinesActivityKind,
  MapMode,
  STEP_ORDER,
  StepKind,
  toMapMode,
  type WizardStep
} from './wizardStep'

const PLAN_SETTING_NAMES = [
  'implementWidth',
  'implementWidthUnit',
  'rowSpacing',
  'rowSpacingUnit',
  'numberOfHeadlands',
  'cornerType',
  'boundaryHoleBehavior',
  'edgeHeadlands',
  'cornerOverrides',
  'holeOverrides',
  'corridorOverrides'
] as const

const HELM_PATH_DEBOUNCE_MS = 250

type LineItem = LineRow & {
  data: SavedPrimitive['data']
}

const readSketch = (
  primitives: FeatureCollection<LineString | Point>
): PropagatableGuidancePrimitive | undefined => {
  const line = primitives.features.find(
    (f) => (f.properties as { zone?: string } | null)?.zone === PRESCRIPTION_BASE_ZONE_ID
  )
  if (line === undefined) return undefined
  // Feature-level props (`type`, `headingDeg`, `radiusFt` — set by decorate +
  // LinePivotCollection) win over the defaults so live geometry edits reach
  // the preview.
  const { zone: _zone, ...props } = (line.properties ?? {}) as {
    zone?: string
    type?: GuidancePrimitive
    [key: string]: unknown
  }
  if (props.type === undefined) return undefined
  return {
    type: props.type,
    primitive: {
      type: 'Feature',
      id: line.id,
      geometry: line.geometry,
      properties: { guidance: { ...DEFAULT_GUIDANCE_PROPERTIES, ...props } }
    }
  } as PropagatableGuidancePrimitive
}

const toSavedLineFeature = (line: LineItem, color: string) => {
  const geometry = savedPrimitiveToLine(line.data)
  if (geometry === null) return []
  return [
    { ...geometry, id: line.id, properties: { kind: 'savedLine', color, type: line.data.type } }
  ]
}

export const GuidanceDrawing = ({
  returnToMain,
  onSuccess,
  fieldId,
  boundaryId,
  planId,
  onGuidancePlanBuilt,
  initialGuidance
}: {
  fieldId: string
  returnToMain: () => void
  // Fires after the guidance plan is saved. Lets the parent transition its
  // own state to leave guidance-drawing mode (returnToMain is the cancel
  // path and doesn't always match the post-save target).
  onSuccess?: () => void
  boundaryId: string
  planId?: string
  // When provided, Save hands the built guidance plan back to the caller
  // instead of writing to the server itself.
  onGuidancePlanBuilt?: (draft: GuidanceDraft) => void
  initialGuidance?: GuidanceDraft
}) => {
  const orgCode = useOrgCodeOrThrow()
  const { t, measureSystem } = useLocalization()
  const { map } = useMapContext()
  const [step, setStep] = useState<WizardStep>(() => enterStep(StepKind.Details))
  const stepIndex = STEP_ORDER.indexOf(step.kind)
  const linesActivity = step.kind === StepKind.Lines ? step.activity : undefined
  const isDrawingNewLine = linesActivity?.kind === LinesActivityKind.Drawing
  const isPickingSide = linesActivity?.kind === LinesActivityKind.PickingSide
  const pickedSide =
    linesActivity?.kind === LinesActivityKind.PickingSide ? linesActivity.picked : null
  const sketch =
    linesActivity?.kind === LinesActivityKind.Drawing ? linesActivity.sketch : undefined
  const draft = step.kind === StepKind.Regions ? step.draft : null
  const setLinesActivity = (activity: LinesActivity) =>
    setStep((prev) => (prev.kind === StepKind.Lines ? { ...prev, activity } : prev))
  const setDraft = (next: Draft | null) =>
    setStep((prev) => (prev.kind === StepKind.Regions ? { ...prev, draft: next } : prev))
  const [overridePopup, setOverridePopup] = useState<OverridePopupState | null>(null)
  const mapSelectedId = useSelectedId()
  const zoneEditing = useCurrentZones()

  // Re-editing saved guidance restores regions and infills from its guidance
  // plan, and which lines are in the plan, in order, from its line sets.
  const initialGuidancePlan = initialGuidance?.plan.data
  // Regions, infills, and per-feature overrides are keyed to the field features
  // the guidance plan was inspected from, so they carry over only while the
  // plan still has that boundary. Otherwise the wizard starts from the current
  // boundary and keeps just the settings and the line list.
  const initialFieldLayout =
    initialGuidancePlan?.boundaryId === boundaryId ? initialGuidancePlan : undefined
  const initialLineIds = useMemo(
    () =>
      initialGuidance === undefined
        ? undefined
        : [
            ...new Set(
              initialGuidance.lineSets.flatMap((lineSet) =>
                lineSet.data.primitives.map(({ id }) => id)
              )
            )
          ],
    [initialGuidance]
  )

  const plansQuery = usePanoramaQuery(
    getFieldPlansByIdApi,
    { orgCode, fieldId },
    { enabled: planId !== undefined }
  )
  const targetPlan = plansQuery.data?.plans.find((p) => p.id === planId)
  const saveGuidancePlan = useSaveGuidancePlan(fieldId, onSuccess)
  const savePrimitive = useSavePrimitive(fieldId)

  // Guidance-primitive selections per zone. Owned here so onSave can snapshot
  // them directly when building the guidance plan. Seeded from initialGuidance
  // so re-edits pick up each region's previously chosen primitive.
  const [zoneGuidance, setZoneGuidance] = useState<Record<string, ZoneGuidance>>(() => {
    if (!initialFieldLayout) return {}
    return Object.fromEntries(
      initialFieldLayout.infillRegions.flatMap((r) =>
        r.regionId !== undefined ? [[r.regionId, { id: r.infill.id }]] : []
      )
    )
  })
  const [justSaved, setJustSaved] = useState<SavedPrimitive[]>([])
  // Lines excluded from this plan. They stay saved field assets (visible in
  // Unassigned); they just don't ride into the line set or get offered in the
  // carve tray.
  const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(new Set())
  // Explicit display and line set order (drag-to-reorder). Lines not present here
  // (newly created, or preexisting field assets) sort after it in their
  // discovery order.
  const [lineOrder, setLineOrder] = useState<string[]>(() => initialLineIds ?? [])
  const guidanceItems = usePanoramaQuery(
    listGuidancePrimitiveApi,
    { orgCode },
    { select: ({ items }) => items.filter((item) => item.fieldId === fieldId) }
  )
  const savedItems = useMemo(() => guidanceItems.data ?? [], [guidanceItems.data])
  // Drop locally-tracked primitives once the refetched list contains them.
  useEffect(() => {
    if (justSaved.length === 0) return
    const known = new Set(savedItems.map((i) => i.id))
    setJustSaved((prev) => prev.filter((p) => !known.has(p.id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- justSaved reads via the functional setter; adding it as a dep would refire the effect on every prune and mask legitimate refetches
  }, [guidanceItems.data])

  // Editing saved guidance: lines on the field that its line sets don't
  // reference start excluded from the plan, matching what was saved.
  const hasSeededLinesRef = useRef(false)
  useEffect(() => {
    if (hasSeededLinesRef.current) return
    if (!initialLineIds) return
    if (!guidanceItems.data) return
    hasSeededLinesRef.current = true
    const lineIds = new Set(initialLineIds)
    setRemovedIds(new Set(guidanceItems.data.filter((i) => !lineIds.has(i.id)).map((i) => i.id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidanceItems.data])

  const isMetric = measureSystem === MeasureSystem.Metric

  const form = useForm<FormValues>({
    defaultValues: {
      name: initialGuidance?.plan.name ?? '',
      lineType: GuidancePrimitive.ABLine,
      implementWidth: isMetric
        ? Math.round(convertFromImperial(initialGuidancePlan?.implementWidth ?? 40, 'ft'))
        : (initialGuidancePlan?.implementWidth ?? 40),
      implementWidthUnit: isMetric ? 'm' : 'ft',
      rowSpacing: isMetric
        ? Math.round(convertFromImperial(initialGuidancePlan?.rowSpacing ?? 30, 'in'))
        : (initialGuidancePlan?.rowSpacing ?? 30),
      rowSpacingUnit: isMetric ? 'cm' : 'in',
      numberOfHeadlands:
        initialGuidancePlan?.globalSettings.exteriorHeadlandCount ??
        DEFAULT_GLOBAL_SETTINGS.exteriorHeadlandCount,
      cornerType:
        initialGuidancePlan?.globalSettings.cornerType ?? DEFAULT_GLOBAL_SETTINGS.cornerType,
      boundaryHoleBehavior:
        initialGuidancePlan?.globalSettings.boundaryHoleBehavior ??
        DEFAULT_GLOBAL_SETTINGS.boundaryHoleBehavior,
      ...(initialFieldLayout === undefined
        ? { edgeHeadlands: [], cornerOverrides: [], holeOverrides: [], corridorOverrides: [] }
        : overrideSettingsFromPlan(initialFieldLayout))
    }
  })

  // Always false at mount — inspect stays disabled until the boundary or the
  // saved regions are in the draw source.
  const [boundaryReady, setBoundaryReady] = useState(false)
  const boundaryQuery = usePanoramaQuery(
    getBoundaryApi,
    { orgCode, boundaryId },
    { enabled: boundaryId !== '' }
  )
  const boundaryLoadedRef = useRef(false)
  useEffect(() => {
    // Hydration (below) runs at mount and sets `boundaryLoadedRef` when it
    // actually places features. Falling through on an empty-hydration case
    // lets this effect load the raw boundary so the map isn't blank when a
    // saved plan's fieldFeatures can't be reconstructed.
    if (boundaryQuery.data === undefined || boundaryLoadedRef.current) return
    boundaryLoadedRef.current = true
    map.drawTool.clear()
    resetZoneEditing()
    map.drawTool.addFeatures(withZone(boundaryQuery.data.data.boundary, PRESCRIPTION_BASE_ZONE_ID))
    syncFromSource(map.drawTool.getFeatureCollection())
    setBoundaryReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryQuery.data])

  const [areas, setAreas] = useState<{
    area: number
    zones: Record<string, number>
  }>(() => {
    const { covered, zones } = Geo.getAreas(map.drawTool.getFeatureCollection())
    return { area: covered, zones }
  })
  const [regionPolygons, setRegionPolygons] = useState<RegionCollection>(() =>
    toInspectRegions(map.drawTool.getFeatureCollection().features)
  )
  const syncFromSource = useCallback((collection: FeatureCollection) => {
    const { covered, zones } = Geo.getAreas(collection)
    setAreas({ area: covered, zones })
    setRegionPolygons(toInspectRegions(collection.features))
  }, [])

  const cornerType = form.watch('cornerType')
  const inspectRequest = useMemo(
    () =>
      boundaryReady && regionPolygons.features.length > 0
        ? { boundary: regionPolygons, cornerType }
        : null,
    [boundaryReady, regionPolygons, cornerType]
  )
  const inspectQuery = useHelmInspect(inspectRequest)
  const holeCount = inspectQuery.data?.holes.length ?? 0
  const corridorCount = inspectQuery.data?.corridors.length ?? 0
  // The boundary asset inspected whole, never carved: the Lines step offers
  // its sides as line sources and previews each line across the whole field.
  const boundaryInspectRequest = useMemo(() => {
    if (boundaryQuery.data === undefined) return null
    const boundary = withZone(boundaryQuery.data.data.boundary, PRESCRIPTION_BASE_ZONE_ID)
    const features = boundary.type === 'FeatureCollection' ? boundary.features : [boundary]
    return {
      boundary: toInspectRegions(features),
      cornerType: DEFAULT_GLOBAL_SETTINGS.cornerType
    }
  }, [boundaryQuery.data])
  const boundaryInspectQuery = useHelmInspect(boundaryInspectRequest)

  // ------------------------------------------------------------------
  // Line list + tray derivation
  // ------------------------------------------------------------------

  const allLines: LineItem[] = useMemo(() => {
    const toItem = (item: {
      id: string
      name: string
      data: SavedPrimitive['data']
    }): LineItem => ({
      id: item.id,
      name: item.name,
      type: item.data.type,
      data: item.data
    })
    const items = savedItems.map(toItem)
    const known = new Set(items.map((i) => i.id))
    return [...items, ...justSaved.filter((p) => !known.has(p.id)).map(toItem)]
  }, [savedItems, justSaved])

  const orderedLines = useMemo(() => {
    const rank = new Map(lineOrder.map((id, index) => [id, index]))
    return [...allLines].sort(
      (a, b) =>
        (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    )
  }, [allLines, lineOrder])

  const includedLines = useMemo(
    () => orderedLines.filter((l) => !removedIds.has(l.id)),
    [orderedLines, removedIds]
  )

  // Which line the Lines step previews via helm; without a choice it previews
  // the default line. Clicking a line sets the preview
  // without reordering the list.
  // A pre-propagated line can't be a region's infill, so it never enters the tray.
  const trayLines = useMemo(() => {
    const placed = new Set(Object.values(zoneGuidance).map((g) => g.id))
    return includedLines.filter(
      (l) => !placed.has(l.id) && l.type !== GuidancePrimitive.PrePropagated
    )
  }, [includedLines, zoneGuidance])

  // The line the remainder region follows: its assignment once the carve step
  // has made one, and until then the line the carve step will assign it.
  const defaultLineId =
    (zoneGuidance[PRESCRIPTION_BASE_ZONE_ID] as ZoneGuidance | undefined)?.id ??
    trayLines[0]?.id ??
    null
  const previewLineId =
    (step.kind === StepKind.Lines ? step.previewLineId : null) ??
    defaultLineId ??
    includedLines[0]?.id ??
    null

  // A pre-propagated line is driven as given, so helm has nothing to preview.
  const previewLinePrimitive = useMemo(() => {
    const data = allLines.find((l) => l.id === previewLineId)?.data
    return data?.type === GuidancePrimitive.PrePropagated ? undefined : data
  }, [previewLineId, allLines])
  const baseSideOptions = useMemo(() => {
    const edges = boundaryInspectQuery.data?.edges ?? []
    return edges.map((edge, i) => ({
      id: edge.id,
      label: t(($) => $.planning.sideLabel, { number: i + 1 }),
      edge
    }))
  }, [boundaryInspectQuery.data?.edges, t])
  // Derived from `pickedSide` + `baseSideOptions` — a picked side is
  // propagated through the same helm/path preview as a drawn line, wrapped
  // in a synthetic Curve primitive.
  const sidePickedPrimitive = useMemo<PropagatableGuidancePrimitive | undefined>(() => {
    if (pickedSide === null) return undefined
    const option = baseSideOptions.find((o) => o.id === pickedSide.id)
    if (option === undefined) return undefined
    return {
      type: GuidancePrimitive.Curve as const,
      primitive: {
        type: 'Feature' as const,
        id: option.edge.id,
        geometry: option.edge.feature.geometry,
        properties: {
          guidance: {
            ...DEFAULT_GUIDANCE_PROPERTIES,
            type: GuidancePrimitive.Curve as const
          }
        }
      }
    }
  }, [pickedSide, baseSideOptions])

  const planSettings = useWatch({ control: form.control, name: PLAN_SETTING_NAMES })
  const [implementWidth, implementWidthUnit] = planSettings
  const swathWidthFt = convertToImperial(implementWidth, implementWidthUnit)
  const linePreviewPrimitive = sidePickedPrimitive ?? sketch ?? previewLinePrimitive
  const fullFieldFeatures = boundaryInspectQuery.data?.regions
  const linePreviewRequest = useMemo(
    () =>
      step.kind !== StepKind.Lines ||
      fullFieldFeatures === undefined ||
      linePreviewPrimitive === undefined
        ? null
        : buildLinePreviewRequest({
            fieldFeatures: fullFieldFeatures,
            primitive: linePreviewPrimitive,
            swathWidthFt
          }),
    [step.kind, fullFieldFeatures, linePreviewPrimitive, swathWidthFt]
  )
  const linePreviewQuery = useHelmPath(linePreviewRequest)

  // Zones the saved plan can actually cover: only zones with claimed area
  // exist in the inspected boundary that helm and the plan reference.
  const zonesWithArea = useMemo(
    () =>
      (zoneEditing ? Array.from(zoneEditing.zones.values()) : []).filter(
        (z) => (areas.zones[z.id] ?? 0) > 0
      ),
    [zoneEditing, areas]
  )

  const draftPlan = useMemo(() => {
    const inspect = inspectQuery.data
    if (inspect === undefined) return null
    const [
      settingsImplementWidth,
      settingsImplementWidthUnit,
      rowSpacing,
      rowSpacingUnit,
      numberOfHeadlands,
      settingsCornerType,
      boundaryHoleBehavior,
      edgeHeadlands,
      cornerOverrides,
      holeOverrides,
      corridorOverrides
    ] = planSettings
    return buildDraftGuidancePlan({
      boundaryId,
      settings: {
        implementWidth: settingsImplementWidth,
        implementWidthUnit: settingsImplementWidthUnit,
        rowSpacing,
        rowSpacingUnit,
        numberOfHeadlands,
        cornerType: settingsCornerType,
        boundaryHoleBehavior,
        edgeHeadlands,
        cornerOverrides,
        holeOverrides,
        corridorOverrides
      },
      inspect,
      regionIds: zonesWithArea.map((z) => z.id),
      zoneGuidance,
      lines: allLines
    })
  }, [inspectQuery.data, planSettings, boundaryId, zonesWithArea, zoneGuidance, allLines])
  const regionsPreviewRequest = useDebouncedValue(
    useMemo(
      () =>
        step.kind === StepKind.Lines || draftPlan === null
          ? null
          : buildPlanPropagationRequest({ plan: draftPlan, primitives: allLines }),
      [step.kind, draftPlan, allLines]
    ),
    HELM_PATH_DEBOUNCE_MS
  )
  const regionsPreviewQuery = useHelmPath(regionsPreviewRequest)

  const isLinesStep = step.kind === StepKind.Lines
  const previewQuery = isLinesStep ? linePreviewQuery : regionsPreviewQuery
  const previewRequest = isLinesStep ? linePreviewRequest : regionsPreviewRequest
  const isPlaceholderLinePreview = isLinesStep && previewQuery.isPlaceholderData
  const previewFeatures =
    previewRequest === null || isPlaceholderLinePreview ? undefined : previewQuery.data?.features
  useEffect(() => {
    map.drawTool.setPropagationPreview(previewFeatures ?? [])
  }, [map.drawTool, previewFeatures])
  const hasPropagationPreview = previewFeatures !== undefined

  // Keep the active line highlighted on the map even when list hover or the
  // map's pointermove clears the selected id.
  useEffect(() => {
    if (!isLinesStep || mapSelectedId !== null || previewLineId === null) return
    map.drawTool.setSelected(previewLineId)
  }, [mapSelectedId, isLinesStep, previewLineId, map.drawTool])

  // arrayMove semantics, matching what dnd-kit reports: the dragged line
  // takes the target's slot, shifting everything between them.
  const reorderLine = (dragId: string, targetId: string) => {
    const ids = orderedLines.map((l) => l.id)
    const from = ids.indexOf(dragId)
    const to = ids.indexOf(targetId)
    if (to === 0) {
      previewLine(dragId)
    }
    if (from === -1 || to === -1 || from === to) return
    ids.splice(to, 0, ...ids.splice(from, 1))
    setLineOrder(ids)
  }

  // The Lines step shows every included line in its palette color. The
  // Regions step shows each region's line in the region's color, plus the
  // tray line selected in the list.
  useEffect(() => {
    const savedLines = () => {
      if (step.kind === StepKind.Lines && !isDrawingNewLine) {
        return includedLines.flatMap((l, i) => toSavedLineFeature(l, planZoneColor(i)))
      }
      if (step.kind !== StepKind.Regions) return []
      const placed = Object.entries(zoneGuidance).flatMap(([zoneId, guidance]) => {
        const line = allLines.find((l) => l.id === guidance.id)
        const color = zoneEditing?.zones.get(zoneId)?.color
        return line === undefined || color === undefined ? [] : toSavedLineFeature(line, color)
      })
      const trayIndex = trayLines.findIndex((l) => l.id === mapSelectedId)
      const selectedTrayLine = trayLines.at(trayIndex)
      const selectedTray =
        trayIndex === -1 || selectedTrayLine === undefined
          ? []
          : toSavedLineFeature(selectedTrayLine, planZoneColor(trayIndex))
      return [...placed, ...selectedTray]
    }
    map.drawTool.setViewOnlyFeaturesByKind('savedLine', savedLines())
    return () => {
      map.drawTool.clearViewOnlyByKind('savedLine')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    includedLines,
    trayLines,
    allLines,
    zoneGuidance,
    zoneEditing,
    step.kind,
    isDrawingNewLine,
    mapSelectedId
  ])

  // ------------------------------------------------------------------
  // Placement: carve / un-carve / pick / remove
  // ------------------------------------------------------------------

  // The in-progress carve: a draft region being shaped for one line. The
  // zone and its line assignment exist for the whole draft (so the preview is
  // live) — Confirm just ends the transaction, Cancel dissolves everything
  // back into the remainder and returns the line to the tray. Cancelling an
  // edit reverts the geometry to the pre-edit state without deleting the
  // zone or clearing its assignment.
  const draftRef = useRef(draft)
  draftRef.current = draft

  const armPickOrCut = useCallback(() => {
    const remainderPieces = map.drawTool
      .getFeatureCollection()
      .features.filter(
        (f) => f.geometry.type === 'Polygon' && f.properties?.zone === PRESCRIPTION_BASE_ZONE_ID
      ).length
    map.drawTool.setDrawStyle(
      remainderPieces > 1
        ? { mode: DrawMode.AssignZone, shape: DrawStyle.Polygon }
        : { mode: DrawMode.Split, shape: DrawStyle.Line }
    )
  }, [map.drawTool])

  const undoCarveStep = () => {
    const cutting =
      map.drawTool.mode === DrawMode.Split || map.drawTool.mode === DrawMode.AssignZone
    map.drawTool.undo()
    if (cutting) armPickOrCut()
  }

  const separateRemainder = () => {
    const { implementWidth, implementWidthUnit } = form.getValues()
    const widthM =
      implementWidthUnit === 'm' ? implementWidth : convertFromImperial(implementWidth, 'ft')
    if (!Number.isFinite(widthM) || widthM <= 0) return
    map.drawTool.mergeZonePolygons(PRESCRIPTION_BASE_ZONE_ID)
    map.drawTool.splitZonePolygonsAtNecks(PRESCRIPTION_BASE_ZONE_ID, widthM)
  }

  const startCarve = (line: LineRow) => {
    const item = allLines.find((l) => l.id === line.id)
    if (!item) return
    const zone = createZone({ name: item.name })
    map.drawTool.clearHistory()
    setZoneGuidance((prev) => ({ ...prev, [zone.id]: { id: item.id } }))
    setDraft({ zoneId: zone.id, lineId: item.id, mode: DraftMode.Create })
  }

  const editCarve = (zoneId: string) => {
    if (zoneId === PRESCRIPTION_BASE_ZONE_ID) return
    const guidance = zoneGuidance[zoneId] as ZoneGuidance | undefined
    if (guidance === undefined) return
    map.drawTool.clearHistory()
    setDraft({ zoneId, lineId: guidance.id, mode: DraftMode.Edit })
    map.drawTool.setSelected(zoneId)
  }

  const confirmCarve = () => {
    for (const zone of zoneEditing?.zones.values() ?? []) {
      map.drawTool.mergeZonePolygons(zone.id)
    }
    setDraft(null)
  }

  const cancelCarve = () => {
    map.drawTool.undoAll()
    if (draft?.mode === DraftMode.Create) {
      clearAssignment(draft.zoneId)
      deleteZone(draft.zoneId)
    }
    setDraft(null)
  }

  // A cut alone claims nothing in the carve flow: after the split lands, the
  // user picks which resulting piece joins the draft region, so the tool
  // hands off to the click-to-assign interaction automatically.
  useEffect(() => {
    const { cleanup } = map.drawTool.onDrawEnd(() => {
      if (draftRef.current === null) return
      if (map.drawTool.mode !== DrawMode.Split) return
      map.drawTool.setDrawStyle({ mode: DrawMode.AssignZone, shape: DrawStyle.Polygon })
    })
    return cleanup
  }, [map.drawTool])

  // The line's tray membership is derived from zoneGuidance, so dropping the
  // assignment alone puts it back in the tray.
  const clearAssignment = (zoneId: string) => {
    setZoneGuidance((prev) => {
      const { [zoneId]: _dropped, ...rest } = prev
      return rest
    })
  }

  const unCarve = (zoneId: string) => {
    if (zoneId === PRESCRIPTION_BASE_ZONE_ID) return
    clearAssignment(zoneId)
    map.drawTool.reassignZoneTo(zoneId, PRESCRIPTION_BASE_ZONE_ID)
    deleteZone(zoneId)
    separateRemainder()
  }

  const removeFromPlan = (lineId: string) => {
    const placedZone = Object.entries(zoneGuidance).find(([, g]) => g.id === lineId)?.[0]
    if (placedZone === PRESCRIPTION_BASE_ZONE_ID) clearAssignment(placedZone)
    else if (placedZone !== undefined) unCarve(placedZone)
    setRemovedIds((prev) => new Set(prev).add(lineId))
    setLineOrder((prev) => [...prev.filter((id) => id !== lineId), lineId])
  }

  const previewLine = (lineId: string) => {
    setStep((prev) => (prev.kind === StepKind.Lines ? { ...prev, previewLineId: lineId } : prev))
    map.drawTool.setSelected(lineId)
  }

  const restoreToPlan = (lineId: string) => {
    setRemovedIds((prev) => {
      const next = new Set(prev)
      next.delete(lineId)
      return next
    })
  }

  const handlePick = (zoneId: string, guidance: ZoneGuidance) => {
    setZoneGuidance((prev) => ({ ...prev, [zoneId]: guidance }))
  }

  // Picking a boundary side happens on the map: the sides light up, hover
  // highlights, and a click chooses one. The chosen side is persisted as a
  // plan line only when the user confirms its (prefilled, editable) name —
  // assets are immutable, so nothing is saved sight-unseen.

  // Keep the picked side highlighted while in side-selection mode: hovering a
  // different side hands the highlight over to it, but as soon as the pointer
  // leaves we re-sync back to the picked side.
  useEffect(() => {
    if (pickedSide === null || mapSelectedId !== null) return
    map.drawTool.setSelected(pickedSide.id)
  }, [mapSelectedId, pickedSide, map.drawTool])

  useEffect(() => {
    if (!isPickingSide) return
    map.drawTool.setViewOnlyFeaturesByKind(
      'edge',
      baseSideOptions.map((option, index) => ({
        ...option.edge.feature,
        properties: {
          ...option.edge.feature.properties,
          kind: 'edge',
          color: planZoneColor(index)
        }
      }))
    )
    return () => {
      map.drawTool.clearViewOnlyByKind('edge')
    }
  }, [isPickingSide, baseSideOptions, map])

  // The wizard owns the draw tool and the zone store while it is open: it
  // leaves nothing behind for the next session, and a remount loads the
  // boundary again.
  useEffect(() => {
    map.drawTool.setSnapping(false)
    return () => {
      map.drawTool.setSnapping(true)
      map.drawTool.setViewOnlyPicking(null)
      map.drawTool.setZonePolygonsHidden(false)
      map.drawTool.clear()
      resetZoneEditing()
      boundaryLoadedRef.current = false
    }
  }, [map])

  const baseSideOptionsRef = useRef(baseSideOptions)
  baseSideOptionsRef.current = baseSideOptions
  const allLinesRef = useRef(allLines)
  allLinesRef.current = allLines
  const previewLineRef = useRef(previewLine)
  previewLineRef.current = previewLine
  const stepKindRef = useRef(step.kind)
  stepKindRef.current = step.kind
  const onViewOnlyPick = useCallback((id: string) => {
    const sideOption = baseSideOptionsRef.current.find((o) => o.id === id)
    if (sideOption) {
      const picked = { id: sideOption.id, label: sideOption.label }
      setStep((prev) =>
        prev.kind === StepKind.Lines && prev.activity.kind === LinesActivityKind.PickingSide
          ? { ...prev, activity: { kind: LinesActivityKind.PickingSide, picked } }
          : prev
      )
      return
    }
    if (!allLinesRef.current.some((l) => l.id === id)) return
    previewLineRef.current(id)
  }, [])
  useMapListener(map.drawTool.viewOnlyPicks, onViewOnlyPick)

  const onViewOnlyPickDetail = useCallback(
    ({
      ids,
      kind,
      clientX,
      clientY
    }: {
      ids: string[]
      kind: string
      clientX: number
      clientY: number
    }) => {
      if (stepKindRef.current !== StepKind.Overrides) return
      // The popup only knows how to render override controls; clicking a
      // guidance primitive (kind 'savedLine') has no controls to show, so
      // skip it rather than opening an empty popup shell.
      if (kind !== 'edge' && kind !== 'corner' && kind !== 'hole' && kind !== 'corridor') return
      setOverridePopup({ featureIds: ids, kind, clientX, clientY })
    },
    []
  )
  useMapListener(map.drawTool.viewOnlyPickWithDetails, onViewOnlyPickDetail)

  // Persist the picked side as a plan line: commit the edge geometry into the
  // draw tool (so useSavePrimitive can read it back off the feature), save
  // it, then let onLineSaved clean the feature up and buffer the result. The
  // seed feature is tagged with no zone so it can't displace a placed line.
  const savePickedSide = async (name: string) => {
    const option = baseSideOptionsRef.current.find((o) => o.id === pickedSide?.id)
    if (!option || savePrimitive.isPending) return
    const featId = crypto.randomUUID()
    try {
      // A two-point boundary side is semantically an AB line: helm propagates
      // parallel straight lines through the region, which is the right shape
      // for infill along a straight border. Saving it as a Curve produced
      // degenerate propagation (parallel curves at zero curvature that helm
      // handles poorly). Sides with intermediate vertices genuinely bend, so
      // those still save as Curve.
      const coords = option.edge.feature.geometry.coordinates
      const isStraight = coords.length === 2
      const lastCoord = coords[coords.length - 1]
      const geometry = isStraight
        ? { type: 'LineString' as const, coordinates: [coords[0], lastCoord] }
        : option.edge.feature.geometry
      const primitiveType = isStraight ? GuidancePrimitive.ABLine : GuidancePrimitive.Curve
      map.drawTool.addGuidancePrimitive(
        {
          type: 'Feature',
          id: featId,
          geometry,
          properties: {}
        },
        primitiveType,
        undefined
      )
      const saved = await savePrimitive.mutateAsync([{ featId, name }])
      const first = saved.at(0)
      if (first !== undefined) onLineSaved(first)
    } catch {
      map.drawTool.removeFeatureById(featId)
    }
  }

  const onLineSaved = (saved: SavedPrimitive) => {
    setJustSaved((prev) => [...prev, saved])
    map.drawTool.removeFeatureById(saved.featId)
    // Move to top of ordering and preview it.
    setLineOrder((prev) => [saved.id, ...prev.filter((id) => id !== saved.id)])
    setStep({
      kind: StepKind.Lines,
      previewLineId: saved.id,
      activity: { kind: LinesActivityKind.Browsing }
    })
  }

  // Restore drawTool + zone store from the guidance plan's field features
  // on mount. Zone polygons are tagged with `properties.zone = regionId` so
  // helm-inspect and AddZone key off the same tag the drawing flow uses.
  useEffect(() => {
    if (!initialFieldLayout) return
    const boundaryFeatures = initialFieldLayout.fieldFeatures.features.flatMap((f) => {
      const props = f.properties as { group?: string; region?: string } | null
      if (props?.group !== 'field_boundary' || props.region === undefined || props.region === '')
        return []
      if (f.geometry.type !== 'Polygon') return []
      const regionId = props.region
      return [{ ...f, id: regionId, properties: { ...props, zone: regionId } }]
    })
    // Empty hydration (plan has no field-boundary features) falls through to
    // the raw-boundary loader above instead of leaving the map blank.
    if (boundaryFeatures.length === 0) return
    boundaryLoadedRef.current = true
    map.drawTool.clear()
    restoreZones(
      boundaryFeatures.map((f, i) => ({
        id: f.id,
        name: `${t(($) => $.planning.region, { count: 1 })} ${i + 1}`,
        color: ZONE_PALETTE[i % ZONE_PALETTE.length]
      }))
    )
    map.drawTool.addFeatures({ type: 'FeatureCollection', features: boundaryFeatures })
    syncFromSource(map.drawTool.getFeatureCollection())
    setBoundaryReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------

  // Surfaced when the plan we're editing can't be resolved after the plans
  // query has settled — see the guard in `onSave`. Rendered next to the
  // mutation error at the bottom of the drawer.
  const [saveError, setSaveError] = useState<string | null>(null)

  const buildDraft = (): GuidanceDraft | null => {
    // Every zone in the plan must carry an explicitly assigned saved line —
    // nothing is defaulted. The carve flow guarantees this by construction
    // (carved regions are born with their line, the remainder is assigned on
    // entry to the carve step), so a miss means the user removed a line and
    // left a region empty.
    const regionsMissingLines = zonesWithArea
      .filter((zone) => !allLines.some((l) => l.id === zoneGuidance[zone.id]?.id))
      .map((zone) =>
        zone.id === PRESCRIPTION_BASE_ZONE_ID ? t(($) => $.planning.carve.remainder) : zone.name
      )
    if (regionsMissingLines.length > 0) {
      setSaveError(
        t(($) => $.planning.carve.missingLine, { regions: regionsMissingLines.join(', ') })
      )
      return null
    }
    const parsed =
      draftPlan === null || inspectQuery.isPlaceholderData
        ? undefined
        : guidancePlanSchema.safeParse(draftPlan)
    if (parsed?.success !== true) {
      setSaveError(inspectQuery.error?.message ?? t(($) => $.messages.errorOccurredTitle))
      return null
    }
    // The plan's lines become one line set, named after the guidance plan.
    const name = form.getValues('name').trim()
    return {
      plan: { name, data: parsed.data },
      lineSets: [
        {
          name,
          data: { primitives: includedLines.map(({ id, name, type }) => ({ id, name, type })) }
        }
      ]
    }
  }

  const finishAndExit = () => {
    setSaveError(null)
    map.drawTool.clear()
    ;(onSuccess ?? returnToMain)()
  }

  const onSave = () => {
    // No plan in scope (BoundaryOptions flow) — individual primitives are
    // already persisted as the user draws them, so Save is just "done":
    // clear the draw tool and hand off to the caller.
    if (!onGuidancePlanBuilt && planId === undefined) {
      finishAndExit()
      return
    }
    const built = buildDraft()
    if (!built) return
    // NewPlan flow: hand the built plan back to the caller who owns the write.
    if (onGuidancePlanBuilt) {
      setSaveError(null)
      onGuidancePlanBuilt(built)
      map.drawTool.clear()
      ;(onSuccess ?? returnToMain)()
      return
    }
    // Scoped edit: write onto the resolved plan. Guard for the plans query
    // not having resolved (or planId missing from the list).
    if (!targetPlan) {
      setSaveError(t(($) => $.messages.errorOccurredTitle))
      return
    }
    setSaveError(null)
    saveGuidancePlan.mutate({ plan: targetPlan, draft: built })
  }

  useEffect(() => {
    const { cleanup } = map.drawTool.onChange((collection) => {
      syncFromSource(collection)
      // addfeature (on drawend) and changefeature (on modify) both fire
      // here — that's what "drawn or edited" resolves to at the source
      // level. Hand the sketched base-zone line to the line preview.
      const sketch = readSketch(map.drawTool.getGuidancePrimitives())
      setStep((prev) =>
        prev.kind === StepKind.Lines && prev.activity.kind === LinesActivityKind.Drawing
          ? { ...prev, activity: { kind: LinesActivityKind.Drawing, sketch } }
          : prev
      )
    })
    return cleanup
  }, [map.drawTool, syncFromSource])

  // Reassign uses `modify` commands under the hood, whose feature-level
  // property changes don't reliably surface through the draw-tool's onChange
  // stream. Listen on the draw tool's explicit reassign channel so the areas
  // and the regions helm-inspect reads follow the new zone geometry.
  const onReassigned = useCallback(() => {
    syncFromSource(map.drawTool.getFeatureCollection())
    if (draftRef.current !== null) armPickOrCut()
  }, [map.drawTool, syncFromSource, armPickOrCut])
  useMapListener(map.drawTool.zoneReassignments, onReassigned)

  const tabs = [
    {
      kind: StepKind.Details,
      tab: t(($) => $.labels.details),
      checkDisabled: () => {
        const { name, implementWidth, rowSpacing, numberOfHeadlands } = form.watch()
        return (
          !name.trim() ||
          !Number.isFinite(implementWidth) ||
          !Number.isFinite(rowSpacing) ||
          !Number.isFinite(numberOfHeadlands)
        )
      }
    },
    {
      kind: StepKind.Lines,
      tab: t(($) => $.planning.lines, { count: 2 }),
      checkDisabled: () => guidanceItems.isLoading || includedLines.length < 1
    },
    {
      kind: StepKind.Regions,
      tab: t(($) => $.planning.region, { count: 2 }),
      // Only the initial load blocks. Errors and background refetches must
      // not deaden Next — they surface in the footer's error area instead.
      checkDisabled: () => inspectQuery.isLoading
    },
    {
      kind: StepKind.Overrides,
      tab: t(($) => $.planning.overrides),
      // Only block Save while the plans query for a scoped edit is still
      // loading. Validating whether there's anything to save happens in
      // `onSave`, which surfaces an error rather than leaving the button
      // disabled in states the user can't easily diagnose.
      checkDisabled: () => planId !== undefined && plansQuery.isLoading
    }
  ] as const

  // Re-render this component on any form value change so the per-tab disabled
  // checks below pick up the latest values.
  form.watch()

  const ensureRemainderZone = () => {
    if (isNil(getZoneById(PRESCRIPTION_BASE_ZONE_ID))) {
      createZone({ id: PRESCRIPTION_BASE_ZONE_ID, name: t(($) => $.planning.carve.remainder) })
    }
  }

  // View-only features are tagged with a `kind` and the layer's style only
  // paints the kind that matches the active scene. Modes that paint into the
  // view-only layer manage their own kind-scoped lifecycle, so no blanket
  // clear is needed here on transitions.
  const mapMode = toMapMode(step)
  useEffect(() => {
    map.drawTool.setZonePolygonsHidden(step.kind === StepKind.Lines)
    switch (mapMode) {
      case MapMode.Details:
        map.drawTool.setSelected(null)
        setDrawScene(DrawScene.Boundary)
        map.drawTool.setViewOnlyPicking(null)
        map.drawTool.disable()
        break
      case MapMode.LinesBrowsing:
        map.drawTool.setSelected(null)
        setDrawScene(DrawScene.Boundary)
        map.drawTool.setViewOnlyPicking('savedLine')
        map.drawTool.disable()
        break
      case MapMode.LinesDrawing:
        // Drawn lines land in the selected zone, and the onChange handler
        // reads the sketch from the remainder, which doesn't exist yet when
        // the Lines step comes before any visit to Regions.
        ensureRemainderZone()
        // AHeading and RadialPivot styles render only in DrawScene.Lines.
        setDrawScene(DrawScene.Lines)
        map.drawTool.setSelected(PRESCRIPTION_BASE_ZONE_ID)
        map.drawTool.setViewOnlyPicking(null)
        break
      case MapMode.LinesPickingSide:
        map.drawTool.setSelected(null)
        setDrawScene(DrawScene.EdgeSelection)
        map.drawTool.setViewOnlyPicking('edge')
        map.drawTool.disable()
        break
      case MapMode.Regions:
        ensureRemainderZone()
        separateRemainder()
        // The carve flow needs the Lines scene to keep zone polygons colored.
        setDrawScene(DrawScene.Lines)
        map.drawTool.setViewOnlyPicking(null)
        map.drawTool.disable()
        break
      case MapMode.RegionsDraft:
        setDrawScene(DrawScene.Lines)
        map.drawTool.setViewOnlyPicking(null)
        armPickOrCut()
        break
      case MapMode.Overrides:
        map.drawTool.setSelected(null)
        setDrawScene(DrawScene.AllOverrides)
        map.drawTool.setViewOnlyPicking(['edge', 'corner', 'hole', 'corridor'])
        map.drawTool.disable()
        break
    }
    map.drawTool.applyDrawingGate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode])

  // Entering the carve step assigns the default line to the remainder: the
  // entry state is "one region, with your first line propagated across it".
  // The remainder row's picker swaps it afterwards.
  useEffect(() => {
    if (step.kind !== StepKind.Regions) return
    if ((zoneGuidance[PRESCRIPTION_BASE_ZONE_ID] as ZoneGuidance | undefined) !== undefined) return
    if ((areas.zones[PRESCRIPTION_BASE_ZONE_ID] ?? 0) === 0) return
    if (defaultLineId === null) return
    handlePick(PRESCRIPTION_BASE_ZONE_ID, { id: defaultLineId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.kind, defaultLineId, areas.zones[PRESCRIPTION_BASE_ZONE_ID]])

  const canNavigate = isIdle(step) && !(step.kind === StepKind.Regions && inspectQuery.isLoading)
  const nextIsBlocked = tabs[stepIndex].checkDisabled() || saveGuidancePlan.isPending
  const canGoTo = (index: number) =>
    index !== stepIndex &&
    canNavigate &&
    (index < stepIndex || (index === stepIndex + 1 && !nextIsBlocked))

  const goToStep = (index: number) => {
    const target = STEP_ORDER.at(index)
    if (target === undefined || !canGoTo(index)) return
    // Leaving the carve tab: a carved region that never claimed territory
    // can't survive into the plan, so its line goes back to the tray rather
    // than silently dropping out at save time.
    if (step.kind === StepKind.Regions) {
      for (const zone of zoneEditing?.zones.values() ?? []) {
        if (zone.id === PRESCRIPTION_BASE_ZONE_ID) continue
        if ((areas.zones[zone.id] ?? 0) === 0) unCarve(zone.id)
      }
    }
    if (target === StepKind.Regions) map.drawTool.setSelected(PRESCRIPTION_BASE_ZONE_ID)
    setOverridePopup(null)
    setStep(enterStep(target))
  }

  const isLastStep = stepIndex === tabs.length - 1

  const isComputingPreview =
    (previewQuery.isFetching || inspectQuery.isFetching) &&
    !inspectQuery.isError &&
    !previewQuery.isError

  const boundaryGone = boundaryReady && regionPolygons.features.length === 0
  if (boundaryGone) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-6 p-8 text-center">
        <img src={shockedCorn} alt="" className="h-48" />
        <div className="flex flex-col gap-2">
          <h2 className="pan-title3">{t(($) => $.planning.guidancePlanErrorTitle)}</h2>
          <p className="text-sm text-gray-600">{t(($) => $.planning.guidancePlanErrorMessage)}</p>
        </div>
        <div className="flex w-full flex-col gap-2">
          <PanoramaButton
            type="button"
            kind={ButtonKind.Primary}
            loading={saveGuidancePlan.isPending}
            onClick={onSave}
          >
            {t(($) => $.buttons.saveWhatYouHave)}
          </PanoramaButton>
          <PanoramaButton
            type="button"
            kind={ButtonKind.Secondary}
            onClick={() => {
              map.drawTool.disable()
              map.drawTool.clear()
              returnToMain()
            }}
          >
            {t(($) => $.buttons.exitGuidanceEditing)}
          </PanoramaButton>
          {(saveGuidancePlan.isError || saveError !== null) && (
            <div className="pt-1 text-sm text-red-600">
              {saveError ??
                saveGuidancePlan.error?.message ??
                t(($) => $.messages.errorOccurredTitle)}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TabGroup
        className="flex min-h-0 flex-1 flex-col"
        selectedIndex={stepIndex}
        onChange={goToStep}
      >
        <TabList className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white p-2">
          {tabs.map(({ tab, kind }, index) => {
            const isSelected = index === stepIndex
            const isDisabled = !isSelected && !canGoTo(index)
            return (
              <Tab key={kind} as={Fragment} disabled={isDisabled}>
                <button
                  type="button"
                  disabled={isDisabled}
                  className={`flex h-10 items-center justify-center rounded transition-all ${
                    isSelected
                      ? 'pan-title5 flex-1 bg-accent-100 px-3 text-accent-700'
                      : 'px-2 text-xs text-gray-500'
                  } ${isDisabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-gray-50 hover:text-gray-800'}`}
                >
                  {tab}
                </button>
              </Tab>
            )
          })}
        </TabList>
        <TabPanels className="relative min-h-0 flex-1 overflow-y-auto">
          {/* Panels are written out explicitly (rather than iterated) so their
              subtree identity is stable across parent re-renders. An inline
              arrow inside a `.map()` would be a new component identity each
              render, remounting the panel body and re-firing any mount effects
              (e.g. ToolsAndShapes resetting the draw mode to AddZone). */}
          <TabPanel unmount={false}>
            <Details form={form} />
          </TabPanel>
          <TabPanel unmount={false}>
            <LineList
              fieldId={fieldId}
              lines={orderedLines}
              removedIds={removedIds}
              defaultLineId={defaultLineId}
              defaultLineName={t(($) => $.planning.lineList.defaultLineName, {
                number: allLines.length + 1
              })}
              onRemove={removeFromPlan}
              onRestore={restoreToPlan}
              onReorder={reorderLine}
              onSelect={previewLine}
              activeLineId={previewLineId}
              isDrawing={isDrawingNewLine}
              onStartDrawing={() =>
                setLinesActivity({ kind: LinesActivityKind.Drawing, sketch: undefined })
              }
              onStopDrawing={() => setLinesActivity({ kind: LinesActivityKind.Browsing })}
              onLineSaved={onLineSaved}
              drawZoneId={PRESCRIPTION_BASE_ZONE_ID}
              hasSides={baseSideOptions.length > 0}
              sidePicking={isPickingSide}
              pickedSide={pickedSide}
              sideSaving={savePrimitive.isPending}
              onStartSidePicking={() =>
                setLinesActivity({ kind: LinesActivityKind.PickingSide, picked: null })
              }
              onCancelSidePicking={() => setLinesActivity({ kind: LinesActivityKind.Browsing })}
              onSavePickedSide={savePickedSide}
              sidePreviewLoading={linePreviewQuery.isFetching}
            />
          </TabPanel>
          <TabPanel unmount={false}>
            <Carve
              areas={areas}
              trayLines={trayLines}
              zoneGuidance={zoneGuidance}
              lineNameById={Object.fromEntries(allLines.map((l) => [l.id, l.name]))}
              remainderZoneId={PRESCRIPTION_BASE_ZONE_ID}
              draft={draft}
              onCarve={startCarve}
              onEditCarve={editCarve}
              onConfirmCarve={confirmCarve}
              onCancelCarve={cancelCarve}
              onUndoCarve={undoCarveStep}
              onUnCarve={unCarve}
              onRemoveFromPlan={removeFromPlan}
              onPick={handlePick}
            />
          </TabPanel>
          <TabPanel unmount={false}>
            <Overrides
              form={form}
              inspectQuery={inspectQuery}
              holeCount={holeCount}
              corridorCount={corridorCount}
            />
          </TabPanel>
        </TabPanels>
      </TabGroup>

      {/* GuidanceLineDrawing owns its own footer while a new line is being
          drawn, and an open carve draft owns Confirm/Cancel — hide ours so
          there aren't two competing button rows. */}
      {isIdle(step) && (
        <div className="bg-white pt-4">
          {hasPropagationPreview && (
            <div className="pb-3">
              <PropagationLegend />
            </div>
          )}
          {/* Helm inspect + path both run in the background whenever the
              boundary or a setting changes. Show a lightweight status line
              above the button row so the user knows the map preview is stale
              until it settles, rather than staring at an old render for a
              beat. */}
          {isComputingPreview && (
            <div className="flex items-center gap-2 border-t border-gray-300 pb-2 pt-4 text-xs text-gray-500">
              <CircleSpinner size="size-3" />
              <span>{t(($) => $.planning.computingPreview)}</span>
            </div>
          )}
          <div
            className={`flex gap-3 pt-4 ${isComputingPreview ? '' : 'border-t border-gray-300'}`}
          >
            <PanoramaButton
              type="button"
              kind={ButtonKind.Secondary}
              disabledWhen={{
                when: !canNavigate,
                reason: t(($) => $.disabledReasons.fillInRequiredFields)
              }}
              icon={stepIndex === 0 ? SpriteMap.CloseCircleIcon : undefined}
              iconLeft
              className="w-full"
              onClick={
                stepIndex === 0
                  ? () => {
                      map.drawTool.disable()
                      map.drawTool.clear()
                      returnToMain()
                    }
                  : () => goToStep(stepIndex - 1)
              }
            >
              {stepIndex === 0 ? t(($) => $.buttons.cancel) : t(($) => $.buttons.back)}
            </PanoramaButton>
            <PanoramaButton
              type="button"
              kind={ButtonKind.Primary}
              disabledWhen={[
                {
                  when: tabs[stepIndex].checkDisabled(),
                  reason: t(($) => $.disabledReasons.fillInRequiredFields)
                },
                {
                  when: saveGuidancePlan.isPending,
                  reason: t(($) => $.disabledReasons.actionInProgress)
                }
              ]}
              loading={
                ((step.kind === StepKind.Regions || step.kind === StepKind.Overrides) &&
                  inspectQuery.isFetching) ||
                saveGuidancePlan.isPending
              }
              className="w-full"
              icon={isLastStep ? SpriteMap.CheckCircleIcon : SpriteMap.ChevronIcon}
              iconRotation={isLastStep ? undefined : IconRotation.Deg90}
              onClick={isLastStep ? onSave : () => goToStep(stepIndex + 1)}
            >
              {isLastStep ? t(($) => $.buttons.save) : t(($) => $.buttons.next)}
            </PanoramaButton>
          </div>
          {(saveGuidancePlan.isError || saveError !== null) && (
            <div className="pt-2 text-sm text-red-600">
              {saveError ??
                saveGuidancePlan.error?.message ??
                t(($) => $.messages.errorOccurredTitle)}
            </div>
          )}
          {/* Without this, an inspect or helm failure just looks like a frozen
              preview. */}
          {(inspectQuery.isError || previewQuery.isError) && (
            <div className="pt-2 text-sm text-red-600">
              {inspectQuery.error?.message ??
                previewQuery.error?.message ??
                t(($) => $.messages.errorOccurredTitle)}
            </div>
          )}
        </div>
      )}
      {overridePopup !== null && (
        <OverrideMapPopup
          popup={overridePopup}
          form={form}
          inspect={inspectQuery.data}
          onClose={() => setOverridePopup(null)}
        />
      )}
    </div>
  )
}
